import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { CreditCardOutlined, PlusOutlined } from '@ant-design/icons';
import { SubscriptionService } from '../../services/api/subscriptionService';
import { FeatureAccessService } from '../../services/api/featureAccessService';
import type { Feature, OwnedUserSubscription, SubscriptionType } from '../../types/subscription';
import { getApiErrorMessage } from '../../utils/apiErrors';

const { Text } = Typography;
const { Option } = Select;

interface UserSubscriptionTabProps {
  userId: string;
}

function normalizeOwnedRow(raw: Record<string, unknown>): OwnedUserSubscription | null {
  const id = typeof raw.id === 'string' ? raw.id : '';
  const userId = typeof raw.user_id === 'string' ? raw.user_id : '';
  const typeId = typeof raw.subscription_type_id === 'string' ? raw.subscription_type_id : '';
  if (!id || !userId) return null;
  return {
    id,
    user_id: userId,
    subscription_type_id: typeId,
    status: typeof raw.status === 'string' ? raw.status : 'active',
    period_start_date:
      typeof raw.period_start_date === 'string' ? raw.period_start_date : undefined,
    period_end_date:
      typeof raw.period_end_date === 'string' || raw.period_end_date === null
        ? (raw.period_end_date as string | null)
        : undefined,
    auto_renew: typeof raw.auto_renew === 'boolean' ? raw.auto_renew : undefined,
    subscription_type:
      raw.subscription_type && typeof raw.subscription_type === 'object'
        ? (raw.subscription_type as SubscriptionType)
        : null,
  };
}

export const UserSubscriptionTab: React.FC<UserSubscriptionTabProps> = ({ userId }) => {
  const [rows, setRows] = useState<OwnedUserSubscription[]>([]);
  const [types, setTypes] = useState<SubscriptionType[]>([]);
  const [effectiveFeatures, setEffectiveFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');

  useEffect(() => {
    if (userId) {
      void load();
      void loadTypes();
      void loadEffectiveFeatures();
    }
  }, [userId]);

  const loadTypes = async () => {
    try {
      const list = await SubscriptionService.getSubscriptionTypes();
      setTypes(list.filter((t) => t.is_active));
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, 'Failed to load subscription types'));
    }
  };

  const loadEffectiveFeatures = async () => {
    try {
      const features = await FeatureAccessService.getUserFeatures(userId);
      setEffectiveFeatures(features);
    } catch {
      setEffectiveFeatures([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const raw = await SubscriptionService.getUserSubscriptions(userId);
      const normalized = (raw as unknown as Record<string, unknown>[])
        .map((r) => normalizeOwnedRow(r))
        .filter((r): r is OwnedUserSubscription => r != null);

      const enriched = await Promise.all(
        normalized.map(async (row) => {
          if (row.subscription_type || !row.subscription_type_id) return row;
          try {
            const detail = await SubscriptionService.getUserSubscriptionById(userId, row.id);
            const typeFromDetail = detail?.subscription_type;
            if (typeFromDetail) {
              return { ...row, subscription_type: typeFromDetail };
            }
          } catch {
            /* keep row */
          }
          return row;
        })
      );
      setRows(enriched);
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, 'Failed to load subscriptions'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedTypeId) {
      message.warning('Select a subscription type');
      return;
    }
    setLoading(true);
    try {
      await SubscriptionService.createUserSubscription({
        user_id: userId,
        subscription_type_id: selectedTypeId,
        period_start_date: new Date().toISOString(),
        auto_renew: true,
      });
      message.success('User subscription created');
      setModalOpen(false);
      setSelectedTypeId('');
      await load();
      await loadEffectiveFeatures();
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, 'Failed to create user subscription'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (subscriptionId: string) => {
    setLoading(true);
    try {
      await SubscriptionService.updateUserSubscription(userId, subscriptionId, {
        status: 'cancelled',
      });
      message.success('User subscription cancelled');
      await load();
      await loadEffectiveFeatures();
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, 'Failed to cancel subscription'));
    } finally {
      setLoading(false);
    }
  };

  const typeById = (id: string) => types.find((t) => t.id === id);

  const columns = [
    {
      title: 'Subscription',
      key: 'subscription',
      render: (record: OwnedUserSubscription) => {
        const type = record.subscription_type ?? typeById(record.subscription_type_id);
        return (
          <Space>
            <CreditCardOutlined />
            <Text strong>{type?.name ?? `Subscription ${record.id.slice(0, 8)}…`}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Type',
      key: 'type',
      render: (record: OwnedUserSubscription) => {
        const type = record.subscription_type ?? typeById(record.subscription_type_id);
        return type?.code ?? 'N/A';
      },
    },
    {
      title: 'Plan features',
      key: 'features',
      render: (record: OwnedUserSubscription) => {
        const type = record.subscription_type ?? typeById(record.subscription_type_id);
        const codes = type?.features?.map((f) => f.code).filter(Boolean) ?? [];
        if (!codes.length) return <Text type="secondary">—</Text>;
        return (
          <Space size={[4, 4]} wrap>
            {codes.slice(0, 6).map((c) => (
              <Tag key={c}>{c}</Tag>
            ))}
            {codes.length > 6 ? <Tag>+{codes.length - 6}</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Period start',
      dataIndex: 'period_start_date',
      key: 'period_start_date',
      render: (date?: string) => (date ? new Date(date).toLocaleString() : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: OwnedUserSubscription) =>
        record.status === 'cancelled' ? (
          <Text type="secondary">—</Text>
        ) : (
          <Popconfirm
            title="Cancel this user-owned subscription?"
            onConfirm={() => void handleCancel(record.id)}
          >
            <Button type="link" danger size="small">
              Cancel
            </Button>
          </Popconfirm>
        ),
    },
  ];

  const assignedTypeIds = new Set(rows.map((r) => r.subscription_type_id).filter(Boolean));
  const availableTypes = types.filter((t) => !assignedTypeIds.has(t.id));

  return (
    <div data-testid="user-subscription-tab">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="User-owned subscription (layer B)"
        description="Creates a first-class user_subscriptions row via POST /subscriptions/users. Seat assignments on a company plan are managed under Company → Subscriptions."
      />
      {effectiveFeatures.length > 0 && (
        <div style={{ marginBottom: 16 }} data-testid="user-effective-features">
          <Text strong>Effective features (user union)</Text>
          <div style={{ marginTop: 8 }}>
            <Space size={[4, 4]} wrap>
              {effectiveFeatures.map((f) => (
                <Tag key={f.code} color="blue">
                  {f.code}
                </Tag>
              ))}
            </Space>
          </div>
        </div>
      )}
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          Add user subscription
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} subscriptions`,
        }}
        locale={{ emptyText: 'No user-owned subscriptions' }}
      />

      <Modal
        title="Add user subscription"
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setSelectedTypeId('');
        }}
        onOk={() => void handleCreate()}
        confirmLoading={loading}
        okText="Create"
        okButtonProps={{ disabled: !selectedTypeId }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Select a catalog subscription type for this user.
        </Text>
        <Select
          style={{ width: '100%' }}
          placeholder="Subscription type"
          value={selectedTypeId || undefined}
          onChange={setSelectedTypeId}
          showSearch
          optionFilterProp="children"
        >
          {availableTypes.map((t) => (
            <Option key={t.id} value={t.id}>
              {t.name} ({t.code})
            </Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
};
